import CheckPermissions from "../../authorization/authorization";
import { buildTree } from "../../services/Functions";
import { cfpBaseUrl, mnrURL } from "../urls";

/**
 * Fetches and stores all subdivisions based on office hierarchy and office code.
 * 
 * This function extracts different levels of office codes from the provided `officeCode`
 * and makes a request to retrieve subdivision details. The retrieved data is stored in
 * local storage under the key `subDivisions`.
 * 
 * @async
 * @function AllSubDivisions
 * @memberof APIs
 * @param {string} token - The authorization token for API access.
 * @param {boolean} officeHierarchy - Determines whether office hierarchy-based extraction should be performed.
 * @param {number|string} officeCode - The office code used to derive subdivision information.
 * @returns {Promise<void>} - A promise that resolves when the operation completes.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * AllSubDivisions("Bearer abc123", true, 15041)
 *   .then(() => console.log("Subdivisions loaded successfully"))
 *   .catch(err => console.error("Error loading subdivisions", err));
 * 
 * @description
 * - Extracts company, circle, division, and subdivision codes from `officeCode` if `officeHierarchy` is `true`.
 * - If `officeHierarchy` is `false` or `officeCode === 10`, a default company code (`15`) is used.
 * - Sends a `POST` request to fetch subdivision details and stores the result in `localStorage` if successful.
 * - Handles fetch errors gracefully by logging them to the console.
 */
export const AllSubDivisions = async (token, officeHierarchy, officeCode) => {
    try {
        let companyCode = null;
        let companyCodeInt = '';
        let circleCode = null;
        let circleCodeInt = '';
        let divisionCode = null;
        let divisionCodeInt = '';
        let subDivisionCode = null;
        let subDivisionCodeInt = '';

        let officeCodeString = officeCode.toString()


        if (officeHierarchy) {
            companyCode = officeCodeString.slice(0, 2)
            companyCodeInt = parseInt(companyCode)
            circleCode = officeCodeString.slice(0, 3)
            circleCodeInt = parseInt(circleCode)
            divisionCode = officeCodeString.slice(0, 4)
            divisionCodeInt = parseInt(divisionCode)
            subDivisionCode = officeCodeString.slice(0, 5)
            subDivisionCodeInt = parseInt(subDivisionCode)
        }

        else {
            companyCode = '15';
        }
        if (officeCode === 10) {
            companyCode = '15';
        }
        //#region Subdivision API
        var formdataSubDivisions = new FormData();
        formdataSubDivisions.append("companyCode", companyCode);
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        var requestOptionsSubDivisions = {
            method: 'POST',
            body: formdataSubDivisions,
            headers: myHeaders,
            redirect: 'follow',
        };
        await fetch(cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails", requestOptionsSubDivisions)
            .then(response => response.json())
            .then(result => {
                if (result.status === "200") {
                    window.localStorage.setItem('subDivisions', JSON.stringify(result.message))
                }
            })
            .catch(error => console.log('error', error));
        //#endregion
    }
    catch (e) {
        console.log(e, 'Unable to load Subdivisions!')
    }
}

export const AllSubDivisionsForAdmin = async (token, officeHierarchy, officeCode) => {
    try {
        // let companyCode = null;
        // let companyCodeInt = '';
        // let circleCode = null;
        // let circleCodeInt = '';
        // let divisionCode = null;
        // let divisionCodeInt = '';
        // let subDivisionCode = null;
        // let subDivisionCodeInt = '';

        // let officeCodeString = officeCode.toString()


        // if (officeHierarchy) {
        //     companyCode = officeCodeString.slice(0, 2)
        //     companyCodeInt = parseInt(companyCode)
        //     circleCode = officeCodeString.slice(0, 3)
        //     circleCodeInt = parseInt(circleCode)
        //     divisionCode = officeCodeString.slice(0, 4)
        //     divisionCodeInt = parseInt(divisionCode)
        //     subDivisionCode = officeCodeString.slice(0, 5)
        //     subDivisionCodeInt = parseInt(subDivisionCode)
        // }

        // else {
        //     companyCode = '15';
        // }
        // if (officeCode === 10) {
        //     companyCode = '15';
        // }
        //#region Subdivision API
        var formdataSubDivisions = new FormData();
        formdataSubDivisions.append("companyCode", officeCode);
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        var requestOptionsSubDivisions = {
            method: 'POST',
            body: formdataSubDivisions,
            headers: myHeaders,
            redirect: 'follow',
        };
        await fetch(cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails", requestOptionsSubDivisions)
            .then(response => response.json())
            .then(result => {
                if (result.status === "200") {
                    window.localStorage.setItem('subDivisions', JSON.stringify(result.message))
                }
            })
            .catch(error => console.log('error', error));
        //#endregion
    }
    catch (e) {
        console.log(e, 'Unable to load Subdivisions!')
    }
}
/**
 * Fetches all roles if the user has the necessary permissions.
 * 
 * This function checks whether the user has permission to access role management.
 * If permitted, it sends a request to retrieve all roles and returns them.
 * 
 * @async
 * @function AllRoles
 * @memberof APIs
 * @param {string} token - The authorization token for API access.
 * @param {Array} permissions - The list of user permissions to validate access.
 * @returns {Promise<Array>} A promise that resolves to an array of roles.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * AllRoles("Bearer abc123", userPermissions)
 *   .then(roles => console.log("Roles fetched:", roles))
 *   .catch(err => console.error("Error fetching roles", err));
 * 
 * @description
 * - Checks if the user has the `"Access Management"` and `"Role Management"` permissions.
 * - If the user has permission, it makes a `POST` request to retrieve all roles.
 * - If the API call is successful and returns status `"200"`, the roles are stored in `allRoles`.
 * - If the request fails, an error is logged to the console.
 */
export const AllRoles = async (token, permissions) => {
    let allRoles = []
    try {

        //#region All Roles API
        if (CheckPermissions(permissions, "Access Management", "Role Management")) {
            var formdata = new FormData();
            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);

            var requestOptions = {
                method: 'POST',
                body: formdata,
                headers: myHeaders,
                redirect: 'follow',
            };

            await fetch(cfpBaseUrl + "role/getAllRoles", requestOptions)
                .then(response => response.json())
                .then(result => {
                    if (result.status === "200") {
                        allRoles = result.data
                    }
                }
                )
                .catch(error => console.log('error', error));
        }
        //#endregion
    }
    catch (e) {
        console.log(e, 'unable to load roles.')
    }

    return allRoles
}
/**
 * Fetches all Customer Facilitation Centers (CFCs) for a given company if the user has the required permissions.
 * 
 * This function checks whether the user has permission to access `"Organization Management"`.
 * If permitted, it sends a request to retrieve all CFCs and returns them.
 * 
 * @async
 * @function AllCFCs
 * @memberof APIs
 * @param {string} token - The authorization token for API access.
 * @param {Array} permissions - The list of user permissions to validate access.
 * @param {string} companyCode - The company code to fetch relevant CFCs.
 * @returns {Promise<Array>} A promise that resolves to an array of CFCs.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * AllCFCs("Bearer abc123", userPermissions, "15")
 *   .then(cfcs => console.log("CFCs fetched:", cfcs))
 *   .catch(err => console.error("Error fetching CFCs", err));
 * 
 * @description
 * - Checks if the user has the `"Organization Management"` permission.
 * - If the user has permission, it makes a `POST` request with `companyCode` to fetch all CFCs.
 * - If the API call is successful and returns status `"200"`, the CFCs are stored in `allCFCs`.
 * - If the request fails, an error is logged to the console.
 */
export const AllCFCs = async (token, permissions, companyCode) => {
    let allCFCs = []
    try {

        //#region All Roles API
        if (CheckPermissions(permissions, "Organization Management")) {
            var formdata = new FormData();
            formdata.append('companyCode', companyCode)
            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);

            var requestOptions = {
                method: 'POST',
                body: formdata,
                headers: myHeaders,
                redirect: 'follow',
            };

            await fetch(cfpBaseUrl + "customerFaciliationPortal/getAllCFCs", requestOptions)
                .then(response => response.json())
                .then(result => {
                    if (result.status === "200") {
                        allCFCs = result.data
                    }
                }
                )
                .catch(error => console.log('error', error));
        }
        //#endregion
    }
    catch (e) {
        console.log(e, 'unable to load CFCs.')
    }

    return allCFCs
}
/**
 * Fetches all permissions from the API and organizes them into a structured tree format.
 * 
 * This function checks if the user has the required permissions for `"Role Management"` 
 * or `"User Management"` under `"Access Management"`. If authorized, it retrieves the permissions 
 * and structures them into a hierarchical format using `buildTree()`.
 * 
 * @async
 * @function AllPermissions
 * @memberof APIs
 * @param {string} token - The authorization token for API access.
 * @param {Array} permissions - The list of user permissions to validate access.
 * @returns {Promise<{allPermissions: Array, sortedAllPermissions: Array}>} 
 *          A promise that resolves to an object containing:
 *          - `allPermissions`: The raw list of permissions fetched from the API.
 *          - `sortedAllPermissions`: The structured permissions tree.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * AllPermissions("Bearer abc123", userPermissions)
 *   .then(({ allPermissions, sortedAllPermissions }) => {
 *     console.log("All Permissions:", allPermissions);
 *     console.log("Sorted Permissions Tree:", sortedAllPermissions);
 *   })
 *   .catch(err => console.error("Error fetching permissions", err));
 * 
 * @description
 * - Checks if the user has the `"Role Management"` or `"User Management"` permission.
 * - If authorized, it makes a `POST` request to fetch all available permissions.
 * - The response data is stored in `allPermissions`, and `buildTree()` is used to structure them.
 * - If the API request fails, an error is logged.
 * - If the API response is not `"200"`, it stores an empty array in `localStorage`.
 */
export const AllPermissions = async (token, permissions) => {
    let allPermissions = []
    let sortedAllPermissions = []
    try {
        //#region All Permissions API
        if (CheckPermissions(permissions, 'Access Management', 'Role Management') || CheckPermissions(permissions, 'Access Management', 'User Management')) {
            var formdata = new FormData();
            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);

            var requestOptions = {
                method: 'POST',
                body: formdata,
                headers: myHeaders,
                redirect: 'follow',
            };

            await fetch(cfpBaseUrl + "role/getPrem", requestOptions)
                .then(response => response.json())
                .then(result => {
                    if (result.status === "200") {
                        allPermissions = result.data
                        sortedAllPermissions = buildTree(allPermissions)
                    }
                    else {
                        window.localStorage.setItem('all-permissions', [])
                    }
                }).catch(error => console.log('error', error));
        }
    }
    catch (e) {
        console.log(e, 'Unable to load all permissions!')
    }
    //#endregion
    return { allPermissions: allPermissions, sortedAllPermissions: sortedAllPermissions }
}
/**
 * Fetches application basic information based on the given search word.
 * 
 * This function sends a `POST` request to the API with a search term and retrieves matching 
 * application information. If successful, it returns the search results.
 * 
 * @async
 * @function SearchAPI
 * @memberof APIs
 * @param {string} token - The authorization token required for API access.
 * @param {string} searchWord - The search term used to find application information.
 * @returns {Promise<Array>} A promise that resolves to an array of searched items.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * SearchAPI("Bearer abc123", "123456")
 *   .then(data => console.log("Search results:", data))
 *   .catch(err => console.error("Error searching for applications", err));
 * 
 * @description
 * - Sends a `POST` request to fetch application basic information using `searchWord`.
 * - If the response status is `"200"`, it returns the retrieved data.
 * - If the request fails, an error is logged to the console.
 */
export const SearchAPI = async (token, searchWord) => {
    let searchedItems = []
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);

    const formdata = new FormData();
    formdata.append("searchById", searchWord);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow"
    };

    await fetch(cfpBaseUrl + "comman/getApplicationBasicInfo", requestOptions)
        .then((response) => response.json())
        .then((result) => {
            if (result.status === "200") {
                searchedItems = result.data
            }
        })
        .catch((error) => console.error(error));
    return searchedItems
}
/**
 * Fetches details of an individual application based on its ID and type.
 * 
 * This function sends a `POST` request to retrieve specific application details 
 * using the provided `applicationId` and `applicationTypeId`. If successful, 
 * it returns the first result from the response data.
 * 
 * @async
 * @function SearchIndividualApplication
 * @memberof APIs
 * @param {string} token - The authorization token required for API access.
 * @param {string} applicationId - The unique identifier of the application to be searched.
 * @param {string} applicationTypeId - The type ID of the application.
 * @returns {Promise<Object>} A promise that resolves to an object containing application details.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * SearchIndividualApplication("Bearer abc123", "456789", "1")
 *   .then(data => console.log("Application Details:", data))
 *   .catch(err => console.error("Error fetching application details", err));
 * 
 * @description
 * - Sends a `POST` request to fetch application details using `applicationId` and `applicationTypeId`.
 * - If the response status is `"200"`, it returns the first object from the retrieved data.
 * - If the request fails, an error is logged to the console.
 */
export const SearchIndividualApplication = async (token, applicationId, applicationTypeId) => {
    let applicationDetail = {}
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);

    const formdata = new FormData();
    formdata.append("searchById", applicationId);
    formdata.append("applicationTypeId", applicationTypeId);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formdata,
        redirect: "follow"
    };

    await fetch(cfpBaseUrl + "comman/getApplicationsByApplicationId", requestOptions)
        .then((response) => response.json())
        .then((result) => {
            if (result.status === "200") {
                applicationDetail = result.data[0]
            }
        })
        .catch((error) => console.error(error));
    return applicationDetail
}
/**
 * Fetches a list of complaints based on a search parameter.
 * 
 * This function sends a `POST` request to search for complaints using a 
 * provided search term. The results are returned as an array of complaint data.
 * 
 * @async
 * @function AllComplaintAPI
 * @memberof APIs
 * @param {string} token - The authorization token required for API access.
 * @param {string} searchWord - The search parameter used to find complaints.
 * @returns {Promise<Array>} A promise that resolves to an array of complaint objects.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * AllComplaintAPI("Bearer abc123", "power outage")
 *   .then(data => console.log("Complaints:", data))
 *   .catch(err => console.error("Error fetching complaints", err));
 * 
 * @description
 * - Sends a `POST` request with `searchWord` as a JSON body parameter.
 * - Includes `Authorization` and `Content-Type` headers.
 * - If successful, it returns an array of complaints from `result.Data`.
 * - If the request fails, an error is logged to the console.
 */
export const AllComplaintAPI = async (token, searchWord) => {
    let searchedItems = []
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
        searchParameter: searchWord,
    });

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
    };

    await fetch(cfpBaseUrl + "lineComplaints/complaintsSearchBar", requestOptions)
        .then((response) => response.json())
        .then((result) => {
            searchedItems = result.Data
        })
        .catch((error) => console.error(error));
    return searchedItems

}
/**
 * Fetches a list of field staff designations.
 * 
 * This function sends a `POST` request to retrieve the available designations 
 * for field staff. The results are returned as an array.
 * 
 * @async
 * @function getFieldStaffDesignations
 * @memberof APIs
 * @param {string} token - The authorization token required for API access.
 * @returns {Promise<Array>} A promise that resolves to an array of field staff designations.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * getFieldStaffDesignations("Bearer abc123")
 *   .then(data => console.log("Field Staff Designations:", data))
 *   .catch(err => console.error("Error fetching designations", err));
 * 
 * @description
 * - Sends a `POST` request to fetch field staff designations.
 * - Includes an `Authorization` header.
 * - If successful, it returns an array of designations from `result.data`.
 * - If the request fails, an error is logged to the console.
 */
export const getFieldStaffDesignations = async (token) => {
    let fieldStaffDesignations = []
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);

    const requestOptions = {
        method: "POST",
        headers: myHeaders,
        redirect: "follow"
    };

    await fetch(cfpBaseUrl + "workForce/getListOfDesignations", requestOptions)
        .then((response) => response.json())
        .then((result) => {
            fieldStaffDesignations = result.data
        })
        .catch((error) => console.error(error));
    return fieldStaffDesignations

}
/**
 * Deletes workforce user data.
 * 
 * This function sends a `POST` request to delete a workforce user by their user ID.
 * 
 * @async
 * @function deleteWFData
 * @memberof APIs
 * @param {Object} WFData - An object containing the user data.
 * @param {number|string} WFData.userId - The ID of the workforce user to be deleted.
 * @param {string} token - The authorization token required for API access.
 * @returns {Promise<void>} A promise that resolves when the deletion request completes.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * deleteWFData({ userId: 12345 }, "Bearer abc123")
 *   .then(() => console.log("User deleted successfully"))
 *   .catch(err => console.error("Error deleting user", err));
 * 
 * @description
 * - Sends a `POST` request to delete a workforce user.
 * - Includes an `Authorization` and `Content-Type` header.
 * - Sends the `userId` in the request body.
 * - If the request fails, an error is logged to the console.
 */
export const deleteWFData = async (WFData, token) => {
    try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "userId": WFData.userId
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        await fetch(cfpBaseUrl + "workForce/deleteUser", requestOptions)
            .then((response) => response.json())
            .then((result) => {
            })
            .catch((error) => console.error(error));
    } catch (e) {
        console.log(e)
    }
}
/**
 * Fetches workforce employee data for a given office code.
 * 
 * This function sends a `POST` request to retrieve a list of employees based on the provided office code.
 * 
 * @async
 * @function getWorkforceData
 * @memberof APIs
 * @param {string} token - The authorization token required for API access.
 * @param {string} code - The office code to filter workforce employees.
 * @returns {Promise<Object[]>} A promise that resolves to an array of employee data objects.
 * 
 * @throws {Error} Logs errors if the fetch request fails.
 * 
 * @example
 * getWorkforceData("Bearer abc123", "OFFICE001")
 *   .then(data => console.log("Workforce Data:", data))
 *   .catch(err => console.error("Error fetching workforce data", err));
 * 
 * @description
 * - Sends a `POST` request with an `officeCode` in the request body.
 * - Includes `Authorization` and `Content-Type` headers.
 * - If the API response contains "Data Found.", the function processes the employee list.
 * - Returns an array of objects, each containing:
 *   - `name` (Employee Name)
 *   - `designation` (Employee Designation ID)
 *   - `employeeCode` (Unique Employee Code)
 *   - `shift` (Shift ID)
 *   - `phoneNo` (Employee Mobile Number)
 *   - `cnic` (Employee CNIC)
 *   - `userId` (User ID)
 *   - `password` (Employee Password)
 * - If an error occurs, it is logged to the console.
 */
export const getWorkforceData = async (token, code) => {
    let WFData = []
    try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "officeCode": code
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        await fetch(cfpBaseUrl + "workForce/getAllEmployeesList", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.message == 'Data Found.') {
                    result.data.forEach((item) => {
                        const userData = {
                            name: item.employeeName,
                            designation: item.employeeDesignationId,
                            employeeCode: item.employeeCode,
                            shift: item.shiftIds,
                            phoneNo: item.employeeMobileNo,
                            cnic: item.employeeCNIC,
                            userId: item.userId,
                            password: item.password,
                            subDivisionCode : item.subDivisionCode
                        };
                        WFData.push(userData)
                    })
                }
            })
            .catch((error) => console.error(error));
    }
    catch (e) {
        console.log(e, ' error in getting wf data')
    }
    return WFData
};
/**
 * Retrieves an authentication token from the MNR API.
 * 
 * This function sends a `POST` request with user credentials to obtain a token.
 * 
 * @async
 * @function getMNRToken
 * @memberof APIs
 * @returns {Promise<string|null>} A promise that resolves to the authentication token if successful, otherwise `null`.
 * 
 * @throws {Error} Logs errors if the request fails or if the response is unsuccessful.
 * 
 * @example
 * getMNRToken()
 *   .then(token => {
 *       if (token) {
 *           console.log("MNR Token:", token);
 *       } else {
 *           console.log("Failed to retrieve token");
 *       }
 *   })
 *   .catch(err => console.error("Error:", err));
 * 
 * @description
 * - Sends a `POST` request to `auth.php` with hardcoded credentials (`user_id: "dev"`, `user_password: "ibs@123"`).
 * - Uses `application/x-www-form-urlencoded` content type.
 * - If the response is successful and `status` is `'1'`, returns the received token.
 * - If the response is unsuccessful, logs the error and returns `null`.
 * - Catches and logs any fetch or parsing errors.
 */
export const getMNRToken = async () => {
    try {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

        const urlencoded = new URLSearchParams();
        urlencoded.append("user_id", "dev");
        urlencoded.append("user_password", "ibs@123");

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: urlencoded,
            redirect: "follow"
        };

        const response = await fetch(mnrURL + "auth.php", requestOptions);

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();

        if (result.status === '1') {
            return result.token;
        } else {
            console.error("Failed to get token:", result);
            return null;
        }
    } catch (error) {
        console.error("Error in getting MNR token:", error);
        return null;
    }
};